<template>
  <div>
    <nav-bar />
    <v-container class="mt-10 pt-5">
      <!-- Title section -->
      <header-page titleText="Doação" titleIcon="fal fa-hand-holding-usd" />
      <v-form v-model="isDonationGroupFormValid">
        <v-container>
          <!-- Card-1 -->
          <v-card elevation="8">
            <v-card-title>
              <v-container>
                <span class="list-labels">Selecione uma Instituição</span>
              </v-container>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row class="mw-form">
                  <v-col>
                    <v-select
                      ref="institutionSelect"
                      :disabled="saving"
                      v-model="selectedInstitution"
                      :items="institutionList"
                      item-text="name"
                      return-object
                      label="Instituição"
                      outlined
                      hide-details="auto"
                      required
                      :rules="[validateInstitution]"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-container>

        <!-- Card-2 -->
        <v-container>
          <v-card elevation="8">
            <!-- Card Title ########################################### -->
            <v-card-title>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="auto">
                    <span v-if="!selectedInstitution" class="list-labels">Lista de doações</span>
                    <span v-else class="list-labels">Lista de doações para <b class="text--primary">{{selectedInstitution.name}}</b></span>
                  </v-col>
                  <v-col class="flex-grow-0 ml-auto">
                    <v-btn
                      ref="addDonationButton"
                      color="secondary"
                      rounded
                      large
                      depressed
                      @click="
                        showNewDonationDialog = true;
                        startNewDonationTour();
                      "
                      :disabled="!selectedInstitution || saving"
                    >
                      <v-icon class="mr-3" color="white" small
                        >fal fa-plus</v-icon
                      >Cadastre a doação
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-title>

            <!-- Card Body ########################################### -->
            <v-card-text v-show="donations.length == 0">
              <v-container>
                <v-row class="mw-form mx-auto">
                  <v-col>
                    <v-alert
                      color="var(--gray)"
                      text
                      border="right"
                      colored-border
                      elevation="0"
                      class="pa-6 px-8 body-2"
                      icon="fal fa-frown"
                      >Ainda não há nenhuma doação...</v-alert
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-list>
              <template v-for="(donation, index) in donations">
                <v-divider
                  v-if="index > 0 && index < donations.length"
                  :key="index"
                ></v-divider>
                <v-list-item :key="'donation' + index" class="py-3 list-group">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        depressed
                        fab
                        icon
                        class="ml-5 icon-rounded"
                        :class="{
                          anonymous: donation.isAnonymous,
                        }"
                      >
                        <v-icon
                          >fal
                          {{
                            donation.isAnonymous ? "fa-user-slash" : "fa-user"
                          }}</v-icon
                        >
                      </v-btn>
                    </template>
                    <span>{{
                      donation.isAnonymous
                        ? "Doação Anônima"
                        : donation.donationFrom.fullName
                    }}</span>
                  </v-tooltip>
                  <v-list-item-content>
                    <v-list-item-title>
                      <h5 class="ml-2">
                        {{ donation.donationFrom.fullName }}
                      </h5>
                    </v-list-item-title>
                  </v-list-item-content>
                  <div
                    class="ml-4 d-flex text-right justify-center align-center"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <div class="description-value red--text" v-on="on">
                          {{ donation.donationValue | FormatMoney }}
                        </div>
                      </template>
                      <span>Valor da doação</span>
                    </v-tooltip>
                    <div class="slip d-flex">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            fab
                            depressed
                            icon
                            class="icon-no-border icon-rounded"
                            @click="editDonation(donation)"
                            :disabled="saving"
                          >
                            <v-icon>fal fa-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Editar</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            fab
                            depressed
                            icon
                            class="icon-no-border icon-rounded hover-red"
                            @click="showRemoveDonationDialog(donation)"
                            :disabled="saving"
                          >
                            <v-icon>fal fa-trash</v-icon>
                          </v-btn>
                        </template>
                        <span>Remover Doação</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-container>
      </v-form>
    </v-container>

    <!-- footer com botão -->
    <template>
      <v-footer fixed color="transparent" padless class="justify-center">
        <v-card elevation="6" class="pt-3 pb-4 px-4 footer-card">
          <v-btn
            :loading="saving"
            color="secondary"
            rounded
            large
            depressed
            class="px-12"
            @click="validateDonationGroupInformation()"
            :disabled="!isDonationGroupInformationValid()"
            >Concluir doação</v-btn
          >
        </v-card>
      </v-footer>
    </template>

    <!-- Modal nova/editar doação -->
    <template>
      <v-dialog v-model="showNewDonationDialog" persistent max-width="490">
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7 primary--text">
              {{ donationActionTitle }} doação em nome de:
            </h5>
          </v-card-title>
          <v-card-text>
            <v-form v-model="isDonationInformationValid">
              <v-row>
                <v-col class="flex-grow-1">
                  <v-combobox
                    ref="donationFromComboBox"
                    v-model="selectedPersonDonationFrom"
                    :items="completePersonDonationFromList"
                    :item-text="documentAndName"
                    return-object
                    label="Digite o CPF ou CNPJ que gostaria de doar:"
                    outlined
                    flat
                    append-icon
                    hide-details="auto"
                    :search-input.sync="documentToSearch"
                    mask="###.###.###-##"
                    :rules="[validateSelectedPerson]"
                    required
                    @change="donationFromComboBoxChanged"
                  ></v-combobox>
                </v-col>
                <v-col class="flex-grow-0">
                  <v-btn
                    ref="addDonationFromButton"
                    fab
                    text
                    outlined
                    small
                    class="gray mt-2"
                    @click="openNewDonorDialog()"
                  >
                    <v-icon small>fal fa-plus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    ref="donationValueTextField"
                    v-model.lazy="donationValue"
                    v-money="money"
                    label="Valor da Doação"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateDonationValue]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <template>
                    <v-combobox
                      ref="donationEmailsComboBox"
                      v-model="donationEmails"
                      chips
                      label="E-mails de recebimento de confirmação"
                      multiple
                      outlined
                      flat
                      append-icon
                      hide-details="auto"
                      deletable-chips
                      :search-input.sync="currentEmail"
                      v-on:keyup.space="addEmail"
                      v-on:keydown.enter="addEmail"
                      v-on:keydown.tab="addEmail"
                      @blur="addEmail"
                    ></v-combobox>
                  </template>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-select
                    ref="paymentMethodSelect"
                    v-model="selectedPaymentMethod"
                    :items="paymentMethodList"
                    label="Meio de pagamento"
                    item-text="name"
                    return-object
                    hide-details="auto"
                    outlined
                    required
                    :rules="[validatePaymentMethod]"
                    :disabled="saving"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row v-if="isBankBilletSelected">
                <v-col cols="12">
                  <InputDueDate
                    v-model="dueDate"
                    :label="'Data de Vencimento'"
                    :rules="[validateDueDate]"
                    required
                    :disabled="saving"
                  />
                </v-col>
              </v-row>

              <div
                class="align-center d-flex px-6"
                v-if="isBankTransferSelected"
              >
                DARF
                <v-spacer></v-spacer>
                <template>
                  <v-sheet>
                    <v-switch v-model="isDARF" inset :disabled="selectedPersonDonationFrom && selectedPersonDonationFrom.isCompany"> </v-switch>
                  </v-sheet>
                  <p>
                    Somente para doações de pessoa física direto pela declaração
                    do IR
                  </p>
                </template>
              </div>

              <v-row class="mw-form" v-if="isBankTransferSelected">
                <v-col  cols="12" class="ml-9">
                  <p><strong>Agência: </strong>{{ council.agency }} (Banco do Brasil) <br>
                    <strong>Conta corrente: </strong>{{ council.account }}-{{council.digit}}<br>
                    <strong>CNPJ/PIX: </strong>{{ formatterHelper.formatCnpj(council.entityViewModel.document)}}
                  </p>
                </v-col>

                <v-col cols="12">
                  <InputDate
                    v-model="depositDate"
                    :label="'Data do Depósito'"
                    :rules="[validateDepositDate]"
                    :minDate="lastYear()"
                    :maxDate="today()"
                    required
                    :disabled="saving"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isBankTransferSelected">
                <v-col cols="12">
                  <v-file-input
                    accept=".png, .jpg, .jpeg, .bmp, .pdf"
                    v-model="selectedDepositFile"
                    label="Enviar comprovante de pagamento"
                    outlined
                    hide-details="auto"
                    prepend-icon="fal fa-paperclip"
                    :rules="[validateDepositFile]"
                    required
                    :disabled="saving"
                  ></v-file-input>
                </v-col>
              </v-row>
              <v-row v-if="isBankTransferSelected">
                <v-col cols="12">
                  <v-textarea
                    v-model="depositObservation"
                    rows="5"
                    row-height="25"
                    auto-grow
                    outlined
                    hide-details="auto"
                    label="Observação"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-divider class="lg"></v-divider>

          <!-- <div class="align-center d-flex px-6">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="gray pr-1" small v-on="on"
                  >fal fa-info-circle</v-icon
                >
              </template>
              <span
                >Uma doação anônima impede que a instituição que recebeu a
                doação veja o nome do doador</span
              >
            </v-tooltip>
            Doação Anônima
            <v-spacer></v-spacer>
            <template>
              <v-sheet>
                <v-switch v-model="anonymousDonationSwitch" inset></v-switch>
              </v-sheet>
            </template>
          </div> -->
          <!-- <v-divider class="lg"></v-divider> -->
          <v-card-actions class="px-6 py-5">
            <v-btn
              rounded
              large
              depressed
              outlined
              class="px-12 gray"
              @click="clearDonationInformation"
              >Voltar</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              rounded
              large
              depressed
              class="px-12"
              @click="saveDonation()"
              :disabled="!isDonationInformationValid"
              >{{ donationActionButton }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- modal remover doação -->
    <template>
      <v-dialog
        v-model="shouldShowRemoveDonationDialog"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Remover doação</h5>
          </v-card-title>
          <v-card-text>Deseja realmente remover esta doação?</v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              large
              depressed
              @click="shouldShowRemoveDonationDialog = false"
              >Não</v-btn
            >
            <v-btn
              color="red"
              text
              large
              depressed
              class="px-12"
              @click="removeDonation()"
              >Sim</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- modal concluir doação -->
    <template>
      <v-dialog
        v-model="shouldShowSaveDonationDialog"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Sua doação está pronta para ser enviada</h5>
          </v-card-title>
          <v-card-text>Deseja concluir a doação?</v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              large
              @click="shouldShowSaveDonationDialog = false"
              >Não</v-btn
            >
            <v-btn
              color="primary"
              text
              large
              class="px-12"
              @click="saveDonations()"
              >Sim</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- modal agradecimento doação -->
    <template>
      <v-dialog v-model="showSuccessMessage" persistent max-width="490">
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Agradecemos a sua doação</h5>
          </v-card-title>
          <v-card-text>{{ successMessage }}</v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              large
              text
              depressed
              @click="confirmSuccessMessage()"
              >Ok</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- modal novo doador -->
    <template>
      <v-dialog
        v-model="showNewDonorDialog"
        scrollable
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5">
              Doador<template v-if="donorAlreadyCreated != null"
                ><br />Por favor confirme os dados de cadastro</template
              >
            </h5>
          </v-card-title>
          <v-card-text>
            <!-- v-if="donorAlreadyCreated == null" -->
            <v-form ref="newDonorForm" v-model="isDonorInformationValid">
              <v-row>
                <v-col>
                  <v-subheader>Dados Básicos</v-subheader>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-mask="['###.###.###-##', '##.###.###/####-##']"
                    v-model="donorDocument"
                    label="CPF/CNPJ"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorDocument]"
                    :disabled="savingNewDonor"
                    @change="donorDocumentChanged($event, donorDocument)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" v-if="donorIsCompany">
                  <v-text-field
                    v-model="donorSocialReason"
                    label="Razão Social"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorSocialReason]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" v-if="donorIsCompany">
                  <v-text-field
                    v-model="donorFullName"
                    label="Nome Fantasia"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorFullName]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" v-if="donorIsNotCompany">
                  <v-text-field
                    v-model="donorFullName"
                    label="Nome"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorFullName]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" v-if="donorIsNotCompany">
                  <v-text-field
                    v-mask="['(##) #####-####', '(##) ####-####']"
                    v-model="donorPhone"
                    label="Celular"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorPhone]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" v-if="donorIsNotCompany">
                  <v-text-field
                    v-model="donorEmail"
                    label="E-mail"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorEmail]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-subheader>endereço</v-subheader>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-mask="'#####-###'"
                    v-model="donorPostalCode"
                    label="CEP"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorPostalCode]"
                    :disabled="savingNewDonor"
                    @blur="findAddressByCEP"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="donorStreet"
                    label="Logradouro"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorStreet]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="donorNumber"
                    label="Número"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorNumber]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="donorComplement"
                    label="Complemento"
                    outlined
                    hide-details="auto"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="donorNeighborhood"
                    label="Bairro"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorNeighborhood]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="donorCity"
                    label="Cidade"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorCity]"
                    :disabled="savingNewDonor"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-combobox
                    :return-object="false"
                    v-model="donorState"
                    :items="stateList"
                    item-value="shortName"
                    item-text="shortName"
                    label="Estado"
                    outlined
                    hide-details="auto"
                    required
                    :rules="[validateNewDonorState]"
                    :disabled="savingNewDonor"
                  ></v-combobox>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-divider class="lg"></v-divider>
          <v-card-actions class="px-6 py-5">
            <v-btn
              rounded
              large
              depressed
              outlined
              class="px-12 gray"
              @click="cancelNewDonorDialog()"
              v-show="!savingNewDonor"
              :disabled="savingNewDonor"
              >Voltar</v-btn
            >
            <v-spacer></v-spacer>
            <!-- v-if="donorAlreadyCreated == null" -->
            <v-btn
              color="secondary"
              rounded
              large
              depressed
              class="px-12"
              :disabled="!isDonorInformationValid"
              @click="showSaveNewDonorConfirmationMessage = true"
              :loading="savingNewDonor"
            >
              Salvar
            </v-btn>
            <v-btn
              v-if="donorAlreadyCreated != null"
              color="secondary"
              rounded
              large
              depressed
              class="px-12"
              :disabled="!isDonorInformationValid"
              @click="selectAlreadyCreatedDonor"
              :loading="savingNewDonor"
            >
              Selecionar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- concluir criação de doador -->
    <template>
      <v-dialog
        v-model="showSaveNewDonorConfirmationMessage"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Confirmar</h5>
          </v-card-title>
          <v-card-text>{{ saveNewDonorConfirmationMessage }}</v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="gray"
              text
              large
              @click="showSaveNewDonorConfirmationMessage = false"
              >Não</v-btn
            >
            <v-btn color="primary" text large @click="saveNewDonor()"
              >Sim</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <!-- doador criado com sucesso -->

    <!-- Modal suas doacoes -->
    <template>
      <v-dialog
        v-model="dialogYourDonations"
        scrollable
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Agradecemos a sua doação</h5>
          </v-card-title>
          <v-card-text>
            {{ successMessage }}
            <v-row v-if="hasDonationWithBankBillet">
              <div class="py-4 pl-4">
                <v-icon class="gray" small>fal fa-exclamation-circle</v-icon>
              </div>

              <v-col>
                <p>
                  Dica: Clique no ícone de código de barras para copiar o
                  código. Aguarde enquanto os códigos são gerados.
                </p>
              </v-col>
            </v-row>
            <v-row>
              <v-list>
                <template v-for="(donation, index) in donations">
                  <v-divider
                    v-if="
                      index > 0 &&
                      index < donations.length &&
                      donation.paymentMethod == paymentMethods.BankBillet
                    "
                    :key="index"
                  ></v-divider>
                  <v-list-item
                    :key="'donation' + index"
                    class="py-3 list-group"
                    v-if="donation.paymentMethod == paymentMethods.BankBillet"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          depressed
                          fab
                          icon
                          class="ml-5 icon-rounded"
                          :class="{
                            anonymous: donation.isAnonymous,
                          }"
                        >
                          <v-icon
                            >fal
                            {{
                              donation.isAnonymous ? "fa-user-slash" : "fa-user"
                            }}</v-icon
                          >
                        </v-btn>
                      </template>
                      <span>{{
                        donation.isAnonymous
                          ? "Doação Anônima"
                          : donation.donationFrom.fullName
                      }}</span>
                    </v-tooltip>
                    <v-list-item-content>
                      <v-list-item-title>
                        <h5 class="ml-2">
                          {{ donation.donationFrom.fullName }}
                        </h5>
                      </v-list-item-title>
                    </v-list-item-content>
                    <div
                      class="ml-4 d-flex text-right justify-center align-center"
                    >
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <div class="description-value red--text" v-on="on">
                            {{ donation.donationValue | FormatMoney }}
                          </div>
                        </template>
                        <span>Valor da doação</span>
                      </v-tooltip>
                      <!-- <div class="slip d-flex">
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              v-on="on"
                              fab
                              depressed
                              icon
                              class="icon-no-border icon-rounded"
                              :loading="!donation.barCode"
                              @click="copyBarCode(donation)"
                            >
                              <v-icon>fal fa-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Copiar código de barras</span>
                        </v-tooltip>
                      </div> -->
                    </div>
                  </v-list-item>
                  <v-list-item :key="'donation_code' + index" v-if="donation.barCode">
                    <v-list-item-content class="ml-5">
                      <label class="secondary--text"
                        >Copie o código do boleto</label
                      >
                      <v-text-field
                        :value="donation.barCode"
                        outlined
                        readonly
                        hide-details="auto"
                        id="bar-code-input"
                      >
                        <template v-slot:append>
                          <v-icon @click="copyBarCode(donation)">
                            mdi-content-copy
                          </v-icon>
                        </template>
                      </v-text-field>

                      <p class="secondary--text">
                        <b>Data de vencimento: </b>
                        {{ formatterHelper.formatDate(donation.billets[0].dueDate) }} 
                      </p>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </v-row>
          </v-card-text>
          <v-divider class="lg" />
          <v-card-actions class="px-6 py-5">
            <v-btn
              large
              rounded
              outlined
              depressed
              class="px-12 gray mx-auto"
              @click="confirmSuccessMessage()"
              >Fechar</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!--###################################### 
        modal sucesso criação de doador
        ######################################-->
    <template>
      <v-dialog
        v-model="showCreatePersonSuccessMessage"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Doador criado</h5>
          </v-card-title>
          <v-card-text>Doador criado/atualizado com sucesso</v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              large
              text
              depressed
              @click="closeNewDonorSuccessMessageDialog()"
              >Ok</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- erro ao criar doador -->
    <template>
      <v-dialog
        v-model="showCreatePersonFailMessage"
        persistent
        max-width="490"
      >
        <v-card>
          <v-card-title>
            <h5 class="pt-5 pb-7">Erro</h5>
          </v-card-title>
          <v-card-text>
            Ocorreu algum erro ao cadastrar o doador.
            <br />Verfique se já existe um doador cadastrado com esse CPF/CNPJ.
          </v-card-text>
          <v-card-actions class="px-6 py-5">
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              large
              text
              depressed
              @click="showCreatePersonFailMessage = false"
              >Ok, entendi!</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar";
import HeaderPage from "@/components/HeaderPage";
import InputDueDate from "@/components/InputDueDate";
import DonationService from "@/scripts/services/donation.service";
import EntityService from "@/scripts/services/entity.service";
import AuthService from "@/scripts/services/auth.service";
import GeneralService from "@/scripts/services/general.service";
import DonationGroup from "@/scripts/models/donations/donationGroup.model";
import PaymentMethods from "@/scripts/enums/paymentMethods.enum";
import CommonHelper from "@/scripts/helpers/common.helper";
import FormatterHelper from "@/scripts/helpers/formatter.helper";
import Routes from "@/router/names.js";
import cep from "cep-promise";
import {mapGetters} from "vuex";

export default {
  name: "New",
  components: {
    NavBar,
    HeaderPage, 
    InputDueDate
  },
  data: () => ({
    formatterHelper: FormatterHelper,
    money: {
      decimal: ",",
      thousands: ".",
      prefix: "",
      suffix: "",
      precision: 2,
    },
    donationService: new DonationService(),
    entityService: new EntityService(),
    authService: new AuthService(),
    generalService: new GeneralService(),
    loggedAgent: {},
    saving: false,
    paymentMethods: PaymentMethods,
    //Dialog controls
    shouldShowRemoveDonationDialog: false,
    shouldShowSaveDonationDialog: false,
    showNewDonationDialog: false,
    dialogYourDonations: false,
    anonymousDonationSwitch: false,
    isDARF: false,
    donationActionButton: "Adicionar",
    donationActionTitle: "Nova",
    showNewDonorDialog: false,
    showSuccessMessage: false,
    successMessage: "",
    //Donation group information
    selectedInstitution: null,
    institutionList: [],
    donations: [],
    savedDonationGroupId: 0,
    //Donation information
    selectedPaymentMethod: null,
    paymentMethodList: [],
    dueDate: null,
    depositDate: null,
    selectedDepositFile: null,
    depositObservation: null,
    currentEmail: "",
    selectedDonation: {},
    donationValue: 0,
    donationEmails: [],
    selectedPersonDonationFrom: null,
    personDonationFromList: [""], //necessário começar com um item para que o componente consiga renderizar a lista
    listDonationsFromOfLoggedAgent: [],
    documentToSearch: "",
    isLoadingPerson: false,
    isDonationInformationValid: false,
    isDonationGroupFormValid: false,
    //Donor information
    isDonorInformationValid: false,
    donorDocument: "",
    donorSocialReason: "",
    donorEmail: "",
    donorFullName: "",
    donorPhone: "",
    donorPostalCode: "",
    donorStreet: "",
    donorNumber: "",
    donorComplement: "",
    donorNeighborhood: "",
    donorCity: "",
    donorState: "",
    donorCountry: "",
    stateList: [],
    donorAlreadyCreated: null,
    //New Donor dialog controls
    showSaveNewDonorConfirmationMessage: false,
    savingNewDonor: false,
    showCreatePersonSuccessMessage: false,
    showCreatePersonFailMessage: false,
    tour: null,
    insideTour: null,
    touredInsideNewDonation: false,
    council: null
  }),
  created() {
    this.entityService.getCouncilById(this.getCouncilId).then(data => (this.council = data));
    this.donationService
      .listAllInstitutionsByLoggedAgent()
      .then((result) => (this.institutionList = result));
    this.donationService.listAllPaymentMethodsByLoggedAgent().then((result) => {
      this.paymentMethodList = result;
      this.changePaymentMethodBaseOnRoute();
    });
    this.authService.getLoggedAgent().then((result) => {
      this.loggedAgent = result;
      if (
        this.loggedAgent != null &&
        this.loggedAgent.personViewModel != null
      ) {
        this.personDonationFromList = [this.loggedAgent.personViewModel];
        //this.selectedPersonDonationFrom = this.loggedAgent.personViewModel;
        this.selectedPersonDonationFrom = null;
      }
    });
    this.generalService
      .listAllStates()
      .then((result) => (this.stateList = result));
    this.donationService
      .listDonationsFromOfLoggedAgent()
      .then((result) => (this.listDonationsFromOfLoggedAgent = result));
  },
  mounted() {
    this.buildStepByStep();
    this.clearDonationInformation();
  },
  computed: {
    ...mapGetters(["getCouncilId","hasCouncilEntityManagementPermission"]),
    saveNewDonorConfirmationMessage() {
      if (this.donorAlreadyCreated)
        return "Deseja atualizar o cadastro do doador?";
      else return "Deseja concluir o cadastro do doador?";
    },
    completePersonDonationFromList() {
      return [
        ...this.personDonationFromList,
        ...this.listDonationsFromOfLoggedAgent,
      ];
    },
    isBankBilletSelected() {
      return (
        this.selectedPaymentMethod &&
        this.selectedPaymentMethod.id == PaymentMethods.BankBillet
      );
    },
    isBankTransferSelected() {
      return (
        this.selectedPaymentMethod &&
        this.selectedPaymentMethod.id == PaymentMethods.BankTransfer
      );
    },
    donorIsCompany() {
      if (
        this.donorAlreadyCreated == null &&
        CommonHelper.validateCNPJ(this.donorDocument)
      )
        return true;
      if (
        this.donorAlreadyCreated &&
        CommonHelper.validateCNPJ(this.donorAlreadyCreated.document)
      )
        return true;
      return false;
    },
    donorIsNotCompany() {
      if (CommonHelper.validateCPF(this.donorDocument)) return true;
      return false;
    },
    hasDonationWithBankBillet() {
      return (
        this.donations.find(
          (donation) => donation.paymentMethod == PaymentMethods.BankBillet
        ) != null
      );
    },
  },
  watch: {
    donorAlreadyCreated(newDonor) {
      console.log(newDonor)
      if (newDonor) {
        this.donorDocument = newDonor.document;
        this.donorFullName = newDonor.fullName;
        this.donorPhone = newDonor.phone;
        this.donorSocialReason = newDonor.socialReason;
        this.donorEmail = newDonor.email;

        this.donorPostalCode = newDonor.addressViewModel.postalCode;
        this.donorState = newDonor.addressViewModel.state;
        this.donorCity = newDonor.addressViewModel.city;
        this.donorStreet = newDonor.addressViewModel.street;
        this.donorNumber = newDonor.addressViewModel.number;
        this.donorComplement = newDonor.addressViewModel.complement;
        this.donorCountry = newDonor.addressViewModel.country;
        this.donorNeighborhood = newDonor.addressViewModel.neighborhood;


        // document: this.donorDocument.replace(/[^\d]+/g, ""),
        // fullName: this.donorFullName,
        // phone: this.donorPhone.replace(/[^\d]+/g, ""),
        // isCompany: this.donorIsCompany,
        // socialReason: this.donorSocialReason,
        // email: this.donorEmail,
        // addressViewModel: {
        // googleAddress: this.donorGoogleAddress,
        // postalCode: this.donorPostalCode.replace(/[^\d]+/g, ""),
        // state: this.donorState,
        // city: this.donorCity,
        // street: this.donorStreet,
        // number: this.donorNumber,
        // complement: this.donorComplement,
        // country: this.donorCountry,
        // neighborhood: this.donorNeighborhood
        // }
      } else {
        this.clearNewDonorData();
      }
    },
    documentToSearch() {
      if (
        this.documentToSearch == null ||
        this.documentToSearch == "" ||
        this.documentToSearch.length < 3
      ) {
        return;
      }

      if (this.validateSelectedPerson() != true) {
        this.selectedPersonDonationFrom = this.documentToSearch = this.formatPartialDocument(
          this.documentToSearch
        );
      }

      if (
        this.documentToSearch == null ||
        this.documentToSearch == "" ||
        this.documentToSearch.length < 3
      ) {
        return;
      }

      if (this.isLoadingPerson) return;

      this.isLoadingPerson = true;

      this.donationService
        .findPersonBySimilarDocument(this.documentToSearch)
        .then((result) => {
          if (result) {
            this.personDonationFromList = [result];
          } else if (this.documentToSearch)
            this.personDonationFromList = [
              "Adicionar novo (" + this.documentToSearch + ")",
            ];
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => (this.isLoadingPerson = false));
    },
  },
  methods: {
    clipboardSuccessHandler({ value }) {
      console.log("success", value);
    },
    findAddressByCEP() {
      cep(this.donorPostalCode).then((response) => {
        this.donorStreet = response.street;
        this.donorState = response.state;
        this.donorCity = response.city;
        this.donorNeighborhood = response.neighborhood;
      });
    },
    clipboardErrorHandler({ value }) {
      console.log("error", value);
    },
    donationFromComboBoxChanged() {
      if(this.selectedPersonDonationFrom != null) {
        //this.openNewDonorDialog();
        if (this.validateSelectedPerson() != true) {
          this.selectedPersonDonationFrom = null;
        }
        this.openEditDonorDialog(this.selectedPersonDonationFrom);
        //this.donorAlreadyCreated = this.selectedPersonDonationFrom;
      }
    },
    formatPartialDocument(document) {
      document = document.replace(/[^\d]+/g, "");
      let result = "";
      if (document.length <= 11) {
        for (let i = 0; i < document.length; i++) {
          if (i == 3 || i == 6) result += ".";
          if (i == 9) result += "-";
          result += document[i];
        }
      } else {
        for (let i = 0; i < document.length; i++) {
          if (i == 2 || i == 5) result += ".";
          if (i == 8) result += "/";
          if (i == 12) result += "-";
          result += document[i];
        }
      }
      return result;
    },
    startNewDonationTour() {
      let donationTourAlreadyDone = localStorage.getItem("donationTourDone");

      if (!donationTourAlreadyDone || donationTourAlreadyDone == false) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            if (!this.touredInsideNewDonation) {
              this.touredInsideNewDonation = true;
              this.insideTour = this.$shepherd({
                useModalOverlay: true,
                // classes: "", // classe para estilizar o componente
              });
              this.insideTour.addStep({
                attachTo: {
                  element: this.$refs.donationFromComboBox.$el,
                  on: "botton",
                },
                text:
                  "Escolha em nome de quem deseja doar digitando o CPF ou CNPJ",
                buttons: [
                  {
                    text: "Próximo",
                    action: this.insideTour.next,
                  },
                  {
                    text: "Fechar",
                    action: this.insideTour.cancel,
                  },
                ],
              });
              this.insideTour.addStep({
                attachTo: {
                  element: this.$refs.addDonationFromButton.$el,
                  on: "top",
                },
                text: "Se você não encontrar, adicione a pessoa ou a empresa",
                buttons: [
                  {
                    text: "Próximo",
                    action: this.insideTour.next,
                  },
                  {
                    text: "Fechar",
                    action: this.insideTour.cancel,
                  },
                ],
              });
              this.insideTour.addStep({
                attachTo: {
                  element: this.$refs.donationValueTextField.$el,
                  on: "top",
                },
                text: "Informe o valor da doação",
                buttons: [
                  {
                    text: "Próximo",
                    action: this.insideTour.next,
                  },
                  {
                    text: "Fechar",
                    action: this.insideTour.cancel,
                  },
                ],
              });
              this.insideTour.addStep({
                attachTo: {
                  element: this.$refs.donationEmailsComboBox.$el,
                  on: "top",
                },
                text:
                  "Informe os e-emails para os quais as informações desta doação serão enviadas",
                buttons: [
                  {
                    text: "Próximo",
                    action: this.insideTour.next,
                  },
                  {
                    text: "Fechar",
                    action: this.insideTour.cancel,
                  },
                ],
              });
              this.insideTour.addStep({
                attachTo: {
                  element: this.$refs.paymentMethodSelect.$el,
                  on: "top",
                },
                text: "Escolha o meio de pagamento e os detalhes do pagamento",
                buttons: [
                  {
                    text: "Fechar",
                    action: this.insideTour.cancel,
                  },
                ],
              });

              if (this.tour) this.tour.cancel();

              this.insideTour.start();

              localStorage.setItem("donationTourDone", true);
            }
          });
        });
      }
    },
    buildStepByStep() {
      let tutorialAlreadyDone = localStorage.getItem("newTutorialDone");
      if (!tutorialAlreadyDone || tutorialAlreadyDone == false) {
        this.$nextTick(() => {
          this.tour = this.$shepherd({
            useModalOverlay: true,
            // classes: "", // classe para estilizar o componente
          });

          this.tour.addStep({
            attachTo: { element: this.$refs.institutionSelect.$el, on: "top" },
            text: "Selecione a instituição para a qual deseja doar",
            buttons: [
              {
                text: "Próximo",
                action: this.tour.next,
              },
              {
                text: "Fechar",
                action: this.tour.cancel,
              },
            ],
          });
          this.tour.addStep({
            attachTo: { element: this.$refs.addDonationButton.$el, on: "top" },
            text: "Adicione uma nova doação",
            buttons: [
              {
                text: "Fechar",
                action: this.tour.cancel,
              },
            ],
          });

          this.tour.start();
        });

        localStorage.setItem("newTutorialDone", true);
      }
    },
    validateInstitution() {
      if (this.selectedInstitution != null && this.selectedInstitution.id > 0)
        return true;
      return "Selecione a instituição para a qual a doação será realizada.";
    },
    validatePaymentMethod() {
      if (
        this.selectedPaymentMethod != null &&
        this.selectedPaymentMethod.id > 0
      )
        return true;
      return "Selecione o meio de pagamento.";
    },
    validateSelectedPerson() {
      if (
        this.selectedPersonDonationFrom != null &&
        this.selectedPersonDonationFrom.id
      )
        return true;
      return "É necessário selecionar em nome de quem você deseja doar.";
    },
    validateDonationValue() {
      if (this.getDonationValueNumber() > 0) return true;
      return "O valor doado deve ser maior do que zero.";
    },
    validateDueDate() {
      if (this.dueDate) {
        return true;
      }
      return "Selecione a data de vencimento.";
    },
    validateDepositDate() {
      if (this.depositDate) {
        return true;
      }
      return "Selecione a data do depósito.";
    },
    validateDepositFile() {
      if (this.selectedDepositFile) {
        return true;
      }
      return "Selecione o comprovante de pagamento.";
    },
    removeEmail(item) {
      this.donationEmails.splice(this.donationEmails.indexOf(item), 1);
      this.donationEmails = [...this.donationEmails];
    },
    validateDonationGroupInformation() {
      if (this.isDonationGroupInformationValid()) {
        this.shouldShowSaveDonationDialog = true;
      }
    },
    saveDonations() {
      if (this.isDonationGroupInformationValid()) {
        this.shouldShowSaveDonationDialog = false;
        this.saving = true;
        let donationGroup = new DonationGroup();
        donationGroup.donationToId = this.selectedInstitution.id;
        // donationGroup.paymentMethod = this.selectedPaymentMethod.id;
        // let addressViewModel = {
        //   postalCode: this.donorPostalCode?.replace(/[^\d]+/g, ""),
        //   state: this.donorState,
        //   city: this.donorCity,
        //   street: this.donorStreet,
        //   number: this.donorNumber,
        //   complement: this.donorComplement,
        //   country: this.donorCountry,
        //   neighborhood: this.donorNeighborhood,
        // };

        donationGroup.donations = [];
        let files = [];
        for (let i = 0; i < this.donations.length; i++) {
          let donationCopy = { ...this.donations[i] };
          //donationCopy.paymentMethod = donationCopy.paymentMethod.id;
          if (donationCopy.depositFile) {
            files.push(donationCopy.depositFile);
            donationCopy.depositFile = null;
          }
          //donationCopy.donationFrom.addressViewModel = addressViewModel;
          if(donationCopy?.donationFrom?.addressViewModel?.postalCode) {
            donationCopy.donationFrom.addressViewModel.postalCode = donationCopy.donationFrom.addressViewModel.postalCode.replace(/[^\d]+/g, "")
          }
          delete donationCopy.depositFile;
          donationGroup.donations.push(donationCopy);
        }
        this.donationService
          .createDonation(donationGroup, files)
          .then(this.saveDonationsCallback)
          .finally(() => (this.saving = false));
      }
    },
    copyBarCode(donation) {
      console.log('click', donation);
      var dummy = document.getElementById("bar-code-input");
      dummy.select();
      document.execCommand("copy");
      // var dummy = document.createElement("textarea");
      // dummy.innerText = donation.barCode;
      // document.body.appendChild(dummy);
      // dummy.select();
      // document.execCommand("copy");
      // dummy.remove();
    },
    loadDonations() {
      this.donationService
        .loadDonationsByDonationGroupId(this.savedDonationGroupId)
        .then(this.loadDonationsCallback);
    },
    loadDonationsCallback(loadedDonations) {
      this.donations = loadedDonations;
      const bankBilletDonationWithoutBarCode = this.donations.find(
        (donation) =>
          donation.paymentMethod == PaymentMethods.BankBillet &&
          !donation.barCode
      );
      if (bankBilletDonationWithoutBarCode != null) {
        setTimeout(this.loadDonations, 1000);
      }
    },
    saveDonationsCallback(donationGroupId) {
      let message = "";
      this.savedDonationGroupId = donationGroupId;
      const bankBilletDonations = this.donations.filter(
        (donation) => donation.paymentMethod == PaymentMethods.BankBillet
      );
      if (bankBilletDonations && bankBilletDonations.length > 0) {
        setTimeout(this.loadDonations, 1000);
        message +=
          "Os boletos foram encaminhados para " +
          this.loggedAgent.email +
          " e também para os emails informados.";
      } else {
        message +=
          "Assim que o conselho confirmar sua doação, um email será enviado para " +
          this.loggedAgent.email +
          " e também para os emails informados.";
      }
      this.successMessage = message;
      this.showSuccessMessage = true;
      this.dialogYourDonations = true;
    },
    confirmSuccessMessage() {
      this.$router.push({ name: Routes.web.ListDonations });
      this.clearDonationGroupInformation();
      this.showSuccessMessage = false;
      this.dialogYourDonations = false;
    },
    clearDonationGroupInformation() {
      this.selectedInstitution = null;
      // this.selectedPaymentMethod = null;
      this.donations = [];
    },
    isDonationGroupInformationValid() {
      return (
        this.isDonationGroupFormValid &&
        this.donations != null &&
        this.donations.length > 0
      );
    },
    changeDonationValue(newValue) {
      if (this.$refs.donationValueTextField) {
        this.$refs.donationValueTextField.$el.getElementsByTagName(
          "input"
        )[0].value = newValue;
        this.donationValue = newValue;
      }
    },
    changePaymentMethodBaseOnRoute() {
      if (this.$route.params.paymentMethod && this.paymentMethodList) {
        if (this.$route.params.paymentMethod == "boleto") {
          this.selectedPaymentMethod = this.paymentMethodList.find(
            (p) => p.id == PaymentMethods.BankBillet
          );
        } else if (this.$route.params.paymentMethod == "transferencia") {
          this.selectedPaymentMethod = this.paymentMethodList.find(
            (p) => p.id == PaymentMethods.BankTransfer
          );
        }
      }
    },
    clearDonationInformation() {
      this.showNewDonationDialog = false;
      this.selectedDonation = {};
      this.changeDonationValue(0);
      this.anonymousDonationSwitch = false;
      this.isDARF = false;
      this.donationEmails = [];
      this.selectedPersonDonationFrom = null;
      this.selectedPaymentMethod = null;
      this.changePaymentMethodBaseOnRoute();
      this.dueDate = null;
      this.depositDate = null;
      this.selectedDepositFile = null;
      this.depositObservation = null;
      this.donationActionTitle = "Nova";
      this.donationActionButton = "Adicionar";
      this.personDonationFromList = [""];
      if (
        this.loggedAgent != null &&
        this.loggedAgent.personViewModel != null
      ) {
        this.personDonationFromList = [this.loggedAgent.personViewModel];
        //this.selectedPersonDonationFrom = this.loggedAgent.personViewModel;
        this.selectedPersonDonationFrom = null;
      }
    },
    getDonationValueNumber() {
      if (
        this.donationValue instanceof String ||
        typeof this.donationValue === "string"
      )
        return parseInt(this.donationValue.replace(/\D/g, ""));
      else if (this.donationValue instanceof Number) return this.donationValue;
      return 0;
    },
    saveDonation() {
      if (this.isDonationInformationValid) {
        this.showNewDonationDialog = false;
        this.selectedDonation.donationValue = this.getDonationValueNumber();
        this.selectedDonation.isAnonymous = this.anonymousDonationSwitch;
        this.selectedDonation.isDARF = this.isDARF;
        this.selectedDonation.donationEmails = this.donationEmails;
        this.selectedDonation.donationFrom = this.selectedPersonDonationFrom;
        this.selectedDonation.paymentMethod = this.selectedPaymentMethod.id;
        this.selectedDonation.dueDate = this.dueDate;
        this.selectedDonation.depositDate = this.depositDate;
        this.selectedDonation.depositFile = this.selectedDepositFile;
        this.selectedDonation.depositObservation = this.depositObservation;

        if (!this.donations.includes(this.selectedDonation)) {
          this.donations.push(this.selectedDonation);
          this.clearDonationInformation();
        }
      }
    },
    editDonation(donation) {
      this.donationActionTitle = "Alterar";
      this.donationActionButton = "Salvar";
      this.selectedDonation = donation;
      this.changeDonationValue(donation.donationValue);
      this.anonymousDonationSwitch = donation.isAnonymous;
      this.isDARF = donation.isDARF;
      this.donationEmails = donation.donationEmails;
      this.selectedPersonDonationFrom = donation.donationFrom;
      this.selectedPaymentMethod = this.paymentMethodList.find(
        (p) => p.id == donation.paymentMethod
      );
      this.dueDate = donation.dueDate;
      this.depositDate = donation.depositDate;
      this.selectedDepositFile = donation.depositFile;
      this.depositObservation = donation.depositObservation;
      this.showNewDonationDialog = true;
    },
    showRemoveDonationDialog(donation) {
      this.selectedDonation = donation;
      this.shouldShowRemoveDonationDialog = true;
    },
    removeDonation() {
      this.donations.splice(this.donations.indexOf(this.selectedDonation), 1);
      this.selectedDonation = {};
      this.shouldShowRemoveDonationDialog = false;
    },
    addEmail() {
      this.$nextTick(() => {
        if (
          this.currentEmail != null &&
          this.currentEmail != "" &&
          !this.donationEmails.includes(this.currentEmail.trim()) &&
          CommonHelper.validateEmail(this.currentEmail.trim())
        ) {
          this.donationEmails.push(this.currentEmail.trim());
        }
        this.$nextTick(() => {
          this.currentEmail = "";
          this.cleanUpInvalidEmails();
        });
      });
    },
    cleanUpInvalidEmails() {
      for (let i = this.donationEmails.length - 1; i >= 0; i--) {
        if (!CommonHelper.validateEmail(this.donationEmails[i])) {
          this.donationEmails.splice(i, 1);
        }
      }
    },
    documentAndName(object) {
      if (object instanceof Object) {
        return (
          FormatterHelper.formatDocument(object.document) +
          " - " +
          object.fullName
        );
      }
      return object;
    },
    today() {
      return new Date();
    },
    ticketLastDueDate() {
      const year = new Date().getFullYear();
      const newYear = new Date(year, 11, 30);
      console.log(newYear);
      return newYear;
    },
    oneYearFromNow() {
      return new Date().getFullYear() == 2023 ? new Date(2023,11,28) : new Date(new Date().setFullYear(new Date().getFullYear() + 1));
    },
    tenDaysFromNow() {
      return this.today().setDate(this.today().getDate() + 10);
    },
    ninetyPastDays() {
      return this.today().setDate(this.today().getDate() - 90);
    },
    thisYear() {
      return new Date(new Date().getFullYear(), 0, 1)
    },
    lastYear() {
      return new Date(new Date().getFullYear()-1, 0, 1)
    },
    openNewDonorDialog() {
      this.clearNewDonorData();

      if (this.validateSelectedPerson() != true) {
        this.donorDocument = this.documentToSearch;
      }
      this.showNewDonorDialog = true;
      this.showNewDonationDialog = false;
    },
    openEditDonorDialog(donorViewModel) {
      //this.clearNewDonorData();

      if (this.validateSelectedPerson() != true) {
        this.donorDocument = this.documentToSearch;
      }

      this.donorAlreadyCreated = donorViewModel;

      this.showNewDonorDialog = true;
      this.showNewDonationDialog = false;
    },
    closeNewDonorDialog() {
      this.showNewDonorDialog = false;
      this.showNewDonationDialog = true;
    },

    cancelNewDonorDialog() {
      this.clearNewDonorData()
      this.documentToSearch = null;
      this.showNewDonorDialog = false;
      this.showNewDonationDialog = true;
    },
    clearNewDonorData() {
      this.donorPostalCode = "";
      this.donorDocument = "";
      this.donorSocialReason = "";
      this.donorEmail = "";
      this.donorFullName = "";
      this.donorPhone = "";
      this.donorStreet = "";
      this.donorNumber = "";
      this.donorComplement = "";
      this.donorNeighborhood = "";
      this.donorCity = "";
      this.donorState = "";
      this.donorCountry = "";
      this.donorAlreadyCreated = null;
      this.isDonorInformationValid = false;
      if (this.$refs.newDonorForm) this.$refs.newDonorForm.reset();
    },
    saveNewDonor() {
      let newDonor = {
        document: this.donorDocument.replace(/[^\d]+/g, ""),
        fullName: this.donorFullName,
        phone: this.donorPhone?.replace(/[^\d]+/g, ""),
        isCompany: this.donorIsCompany,
        socialReason: this.donorSocialReason,
        email: this.donorEmail,
        addressViewModel: {
          postalCode: this.donorPostalCode.replace(/[^\d]+/g, ""),
          state: this.donorState,
          city: this.donorCity,
          street: this.donorStreet,
          number: this.donorNumber,
          complement: this.donorComplement,
          country: this.donorCountry,
          neighborhood: this.donorNeighborhood,
        },
      };
      this.savingNewDonor = true;
      this.showSaveNewDonorConfirmationMessage = false;
      if (!this.donorAlreadyCreated)
        this.authService
          .createPerson(newDonor)
          .then(this.createPersonCallback)
          .catch(this.createPersonCatch)
          .finally(() => (this.savingNewDonor = false));
      else {
        newDonor.id = this.donorAlreadyCreated.id;
        this.authService
          .updatePerson(newDonor)
          .then(this.createPersonCallback)
          .catch(this.createPersonCatch)
          .finally(() => (this.savingNewDonor = false));
      }
    },
    selectAlreadyCreatedDonor() {
      this.personDonationFromList = [this.donorAlreadyCreated];
      this.selectedPersonDonationFrom = this.donorAlreadyCreated;
      this.donorAlreadyCreated = null;
      this.closeNewDonorDialog();
    },
    createPersonCallback(person) {
      this.showCreatePersonSuccessMessage = true;
      this.personDonationFromList = [person];
      this.selectedPersonDonationFrom = person;
    },
    donorDocumentChanged(event, v) {
      if (this.validateNewDonorDocument() == true) {
        this.donationService.findPersonBySimilarDocument(v).then((result) => {
          if (result) {
            this.donorAlreadyCreated = result;
            this.donorDocument = FormatterHelper.formatDocument(
              this.donorAlreadyCreated.document
            );
          } else this.donorAlreadyCreated = null;
        });
      }
    },
    closeNewDonorSuccessMessageDialog() {
      this.showCreatePersonSuccessMessage = false;
      this.closeNewDonorDialog();
    },
    createPersonCatch() {
      this.showCreatePersonFailMessage = true;
    },
    validateNewDonorDocument() {
      if (!this.donorDocument) return "Preencha o documento";
      if (
        !CommonHelper.validateCPF(this.donorDocument) &&
        !CommonHelper.validateCNPJ(this.donorDocument)
      )
        return "CPF/CNPJ inválido";
      return true;
    },
    validateNewDonorSocialReason() {
      if (!this.donorSocialReason) return "Preencha a razão social";
      return true;
    },
    validateNewDonorEmail() {
      this.donorEmail = this.donorEmail.trim();
      if (!this.donorEmail) return "Preencha o e-mail";
      if (!CommonHelper.validateEmail(this.donorEmail))
        return "E-mail inválido";
      return true;
    },
    validateNewDonorFullName() {
      if (!this.donorFullName) return "Preencha o nome";
      if (CommonHelper.wordCount(this.donorFullName) < 2)
        return "Informe o nome completo";
      return true;
    },
    validateNewDonorPhone() {
      if (!this.donorPhone) return "Preencha o celular";
      return true;
    },
    validateNewDonorPostalCode() {
      if (!this.donorPostalCode) return "Preencha o CEP";
      return true;
    },
    validateNewDonorStreet() {
      if (!this.donorStreet) return "Preencha o logradouro";
      return true;
    },
    validateNewDonorNumber() {
      if (!this.donorNumber) return "Preencha o número";
      return true;
    },
    validateNewDonorNeighborhood() {
      if (!this.donorNeighborhood) return "Preencha o bairro";
      return true;
    },
    validateNewDonorCity() {
      if (!this.donorCity) return "Preencha a cidade";
      return true;
    },
    validateNewDonorState() {
      if (!this.donorState) return "Preencha o estado";
      if (!this.stateList.find((state) => state.shortName == this.donorState))
        return "Selecione um estado da lista";
      return true;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.tour) this.tour.cancel();
    if (this.insideTour) this.insideTour.cancel();
    next();
  },
};
</script>
<style scoped>
@import "~shepherd.js/dist/css/shepherd.css";
</style>
